<template>
  <controller>
    <page-header title="Unidades">
      <template v-slot:side>
        <router-link class="btn btn-primary" :to="{ name: 'Nova unidade' }">
          <span>Nova Unidade</span>&nbsp;
        </router-link>
      </template>
    </page-header>

    <main class="units-page">

      <section class="filter-panel" aria-labelledby="filters-title">
        <div class="filter-heading">
          <div>
            <h2 id="filters-title">Buscar unidades</h2>
            <p>Use nome, sigla, descrição, CNPJ ou cidade.</p>
          </div>
          <button type="button" class="btn btn-secondary" :disabled="loading || !hasFilters" @click="clearFilters">Limpar filtros</button>
        </div>
        <div class="filters">
          <label class="search-field">
            <span>Busca</span>
            <input v-model.trim="filters.busca" type="search" placeholder="Ex.: CSC ou Unidade Saúde" @input="scheduleLoad">
          </label>
          <label>
            <span>Cidade</span>
            <select v-model="filters.cidade" @change="loadUnits">
              <option value="">Todas</option>
              <option v-for="city in cityOptions" :key="city" :value="city">{{ city }}</option>
            </select>
          </label>
          <label>
            <span>Status</span>
            <select v-model="filters.status" @change="loadUnits">
              <option value="todos">Todos</option>
              <option value="ativas">Ativas</option>
              <option value="inativas">Inativas</option>
            </select>
          </label>
          <label>
            <span>Tipo de unidade</span>
            <select v-model="filters.educacional" @change="loadUnits">
              <option value="todos">Todos</option>
              <option value="sim">Educacionais</option>
              <option value="nao">Não educacionais</option>
            </select>
          </label>
        </div>
      </section>

      <section class="summary" aria-label="Resumo das unidades">
        <div><strong>{{ meta.total }}</strong><span>Total</span></div>
        <div><strong>{{ meta.ativas }}</strong><span>Ativas</span></div>
        <div><strong>{{ meta.inativas }}</strong><span>Inativas</span></div>
      </section>

      <form-spinner v-if="loading" aria-label="Carregando unidades" />
      <section v-else-if="loadError" class="state-panel error" role="alert">
        <h2>Não foi possível carregar as unidades</h2>
        <p>{{ loadError }}</p>
        <button type="button" class="btn btn-secondary" @click="loadUnits">Tentar novamente</button>
      </section>
      <section v-else-if="!units.length" class="state-panel">
        <h2>Nenhuma unidade encontrada</h2>
        <p>Ajuste os filtros ou cadastre uma nova unidade.</p>
        <router-link class="btn btn-primary" :to="{ name: 'Nova unidade' }">Nova unidade</router-link>
      </section>

      <section v-else class="results-panel" aria-labelledby="results-title">
        <div class="results-heading">
          <div>
            <h2 id="results-title">Resultado</h2>
            <p aria-live="polite">{{ units.length }} unidade(s) encontrada(s)</p>
          </div>
        </div>

        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Unidade</th>
                <th>Localização</th>
                <th>Status</th>
                <th class="actions-heading">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="unit in units" :key="unit.idUnidade">
                <td>
                  <strong>{{ unit.descricao }}</strong>
                  <small>{{ unit.sigla || 'Sem sigla' }} · {{ formatDocument(unit.cnpj) }}</small>
                </td>
                <td>
                  <span>{{ unit.cidade || 'Cidade não informada' }}</span>
                  <small>{{ unit.uf || 'UF não informada' }}</small>
                </td>
                <td><span :class="['status-badge', { inactive: !unit.ativa }]">{{ unit.ativa ? 'Ativa' : 'Inativa' }}</span></td>
                <td class="row-actions">
                  <router-link class="btn btn-primary btn-small" :to="{ name: 'Editar unidade', params: { idUnidade: unit.idUnidade } }">Editar</router-link>
                  <button type="button" class="btn btn-secondary btn-small" :disabled="processingId === unit.idUnidade" @click="remove(unit)">Excluir</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mobile-list">
          <article v-for="unit in units" :key="`mobile-${unit.idUnidade}`">
            <header>
              <div><strong>{{ unit.descricao }}</strong><small>{{ unit.sigla || 'Sem sigla' }}</small></div>
              <span :class="['status-badge', { inactive: !unit.ativa }]">{{ unit.ativa ? 'Ativa' : 'Inativa' }}</span>
            </header>
            <dl>
              <div><dt>Localização</dt><dd>{{ unit.cidade || 'Não informada' }}<template v-if="unit.uf"> / {{ unit.uf }}</template></dd></div>
              <div><dt>CNPJ</dt><dd>{{ formatDocument(unit.cnpj) }}</dd></div>
              <div><dt>Tipo</dt><dd>{{ unit.educacional ? 'Educacional' : 'Não educacional' }}</dd></div>
            </dl>
            <footer>
              <router-link class="btn btn-primary" :to="{ name: 'Editar unidade', params: { idUnidade: unit.idUnidade } }">Editar</router-link>
              <button type="button" class="btn btn-secondary" :disabled="processingId === unit.idUnidade" @click="remove(unit)">Excluir</button>
            </footer>
          </article>
        </div>
      </section>
    </main>
  </controller>
</template>

<script>
import UnidadesService from '@/services/unidades.service'

export default {
  name: 'Unidades',
  data () {
    return {
      units: [],
      loading: true,
      loadError: '',
      processingId: null,
      searchTimer: null,
      cityOptions: [],
      filters: { busca: '', cidade: '', status: 'ativas', educacional: 'todos' },
      meta: { total: 0, ativas: 0, inativas: 0 }
    }
  },
  computed: {
    hasFilters () {
      return Boolean(this.filters.busca || this.filters.cidade) || this.filters.status !== 'todos' || this.filters.educacional !== 'todos'
    }
  },
  mounted () {
    this.loadUnits()
  },
  beforeUnmount () {
    clearTimeout(this.searchTimer)
  },
  methods: {
    scheduleLoad () {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(this.loadUnits, 300)
    },
    async loadUnits () {
      this.loading = true
      this.loadError = ''
      try {
        const response = await UnidadesService.list(this.filters)
        this.units = response.data.data || []
        if (!this.filters.cidade) {
          this.cityOptions = [...new Set(this.units.map(unit => unit.cidade).filter(Boolean))]
            .sort((first, second) => first.localeCompare(second, 'pt-BR'))
        }
        this.meta = response.data.meta || { total: 0, ativas: 0, inativas: 0 }
      } catch (error) {
        this.loadError = error.response?.data?.message || 'Verifique sua conexão e tente novamente.'
      } finally {
        this.loading = false
      }
    },
    clearFilters () {
      this.filters = { busca: '', cidade: '', status: 'todos', educacional: 'todos' }
      this.loadUnits()
    },
    formatDocument (value) {
      if (!value) return 'CNPJ não informado'
      const digits = String(value).replace(/\D/g, '')
      if (digits.length !== 14) return value
      return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
    },
    async deactivate (unit) {
      if (!window.confirm(`Desativar a unidade “${unit.descricao}”? Os dados e vínculos serão preservados.`)) return
      this.processingId = unit.idUnidade
      try {
        await UnidadesService.update(unit.idUnidade, { ativa: false })
        window.toaster.emit('open', { style: 'success', message: 'Unidade desativada com sucesso.', floater: true })
        await this.loadUnits()
      } catch (error) {
        window.toaster.emit('open', { style: 'error', message: error.response?.data?.message || 'Não foi possível desativar a unidade.', floater: true })
      } finally {
        this.processingId = null
      }
    },
    async remove (unit) {
      if (!window.confirm(`Excluir permanentemente a unidade “${unit.descricao}”? Esta ação só será permitida se não houver registros relacionados.`)) return
      this.processingId = unit.idUnidade
      try {
        await UnidadesService.delete(unit.idUnidade)
        window.toaster.emit('open', { style: 'success', message: 'Unidade excluída com sucesso.', floater: true })
        await this.loadUnits()
      } catch (error) {
        window.toaster.emit('open', { style: 'error', message: error.response?.data?.message || 'Não foi possível excluir a unidade.', floater: true })
      } finally {
        this.processingId = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.units-page { max-width: 1440px; margin: 0 auto; padding: $mg_mid; color: $color-dev; }
.filter-heading, .results-heading { display: flex; align-items: center; justify-content: space-between; gap: $mg_small; }
.filter-heading p, .results-heading p { margin: 0; color: $color-text; }
.summary { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: $mg_small; margin-bottom: $mg_mid; }
.summary div { display: flex; align-items: baseline; gap: $mg_mini; padding: $hmg_small $hmg_mid; background: $color-secondary; border: $border-component; border-radius: $border-radius; }
.summary strong { font-size: 24px; }
.summary span { color: $color-text; }
.filter-panel, .results-panel, .state-panel { background: $color-secondary; border: $border-component; border-radius: $border-radius; padding: $hmg_mid; margin-bottom: $mg_mid; }
.filter-heading h2, .results-heading h2, .state-panel h2 { margin: 0 0 $hmg_mini; font-size: 18px; }
.filters { display: grid; grid-template-columns: minmax(260px, 2fr) repeat(3, minmax(160px, 1fr)); gap: $mg_small; margin-top: $hmg_mid; }
label { display: flex; flex-direction: column; gap: $hmg_mini; font-weight: 600; }
input, select { width: 100%; min-height: 44px; padding: 0 $mg_small; border: $border-component; border-radius: $border-radius-mid; background: $color-secondary; color: $color-dev; }
input:focus-visible, select:focus-visible, button:focus-visible, a:focus-visible { outline: 3px solid $color-success; outline-offset: 2px; }
.state-panel { text-align: center; padding: $mg; }
.state-panel p { color: $color-text; }
.state-panel.error { border-color: $color-error !important; }
.results-heading { margin-bottom: $hmg_small; }
.table-wrap { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; }
th, td { padding: $mg_small; border-bottom: $border-component; text-align: left; vertical-align: middle; }
th { color: $color-text; font-size: $font-size-small; font-weight: 700; }
td strong, td span, td small { display: block; }
td small { color: $color-text; margin-top: $hmg_mini; }
.actions-heading { text-align: center; }
.row-actions { display: flex; justify-content: center; flex-wrap: wrap; gap: $hmg_mini; }
.row-actions .btn { display: inline-flex; align-items: center; justify-content: center; text-align: center; }
.status-badge { display: inline-flex; align-items: center; min-height: 28px; padding: 0 $mg_mini; border-radius: $border-radius-xlarge; background: $color-active-light; color: $color-dev; font-size: $font-size-small; font-weight: 700; }
.status-badge.inactive { background: $color-bg; border: $border-component; }
.btn-small { min-height: 36px; padding: $hmg_mini $mg_mini; }
.mobile-list { display: none; }
@media (max-width: 1024px) {
  .units-page { padding: $hmg_mid; }
  .filters { grid-template-columns: 1fr 1fr; }
  .search-field { grid-column: 1 / -1; }
  .table-wrap { display: none; }
  .mobile-list { display: grid; gap: $mg_small; }
  .mobile-list article { border: $border-component; border-radius: $border-radius-mid; padding: $hmg_small; }
  .mobile-list header { display: flex; justify-content: space-between; gap: $mg_small; }
  .mobile-list header small { display: block; color: $color-text; margin-top: $hmg_mini; }
  .mobile-list dl { margin: $mg_small 0; }
  .mobile-list dl div { display: grid; grid-template-columns: 110px 1fr; gap: $mg_mini; padding: $hmg_mini 0; }
  .mobile-list dt { color: $color-text; }
  .mobile-list dd { margin: 0; }
  .mobile-list footer { display: flex; flex-wrap: wrap; gap: $mg_mini; }
}
@media (max-width: 640px) {
  .units-page { padding: $mg_small; }
  .filter-heading { align-items: stretch; flex-direction: column; }
  .filter-heading .btn { width: 100%; min-height: 44px; }
  .summary { grid-template-columns: 1fr; }
  .filters { grid-template-columns: 1fr; }
  .search-field { grid-column: auto; }
  .mobile-list footer .btn { flex: 1 1 100%; min-height: 44px; }
}
</style>
